/**
 * Aircraft Management Section
 * Settings page section for managing aircraft fleet.
 *
 * Requirements: 12.1, 12.2, 12.3, 12.4, 13.1–13.7, 14.1–14.5, 15.1, 15.2, 15.5, 15.6, 22.2, 22.3, 22.8
 */

import { createDataGrid } from '../data-grid.js';
import { openDetailModal } from '../detail-modal.js';
import {
  getAircraft,
  createAircraft,
  updateAircraft,
  retireAircraft,
} from '../../services/api.js';
import { validateAircraftPayload } from '../../utils/validation.js';
import { createTankListEditor } from '../tank-list-editor.js';
import { FUEL_FLOW_UNITS } from '../../utils/constants.js';

// ── Pure helper functions (exported for testing) ────────────────────────────

/**
 * Formats the Hours Note column value for the Aircraft Management grid.
 * Returns an HTML string with a warning indicator when hours exceed 45.
 * @param {*} v - The hours_since_last_50hr value
 * @returns {string} HTML string for the Hours Note cell
 */
export function formatHoursNote(v) {
  const hrs = v != null ? Number(v) : 0;
  if (hrs > 45) {
    return `<span class="hours-alert">${hrs.toFixed(1)} hrs — ⚠ 50hr check due</span>`;
  }
  return `${hrs.toFixed(1)} hrs since 50hr`;
}

/**
 * Returns the CSS class for unserviceable aircraft rows.
 * @param {Object} aircraft
 * @returns {string|null}
 */
export function getAircraftRowClass(aircraft) {
  return aircraft.status === 'UNSERVICEABLE' ? 'aircraft-row--unserviceable' : null;
}

/**
 * Returns default values for a new aircraft.
 * @param {Object} [overrides] - Optional overrides for specific fields
 * @returns {Object}
 */
export function getDefaultAircraftValues(overrides = {}) {
  return {
    status: 'SERVICEABLE',
    airframe_hours: 0,
    engine_hours: 0,
    propeller_hours: 0,
    hours_since_last_50hr: 0,
    ...overrides,
  };
}

/**
 * Checks whether a registration is unique among existing aircraft.
 * Excludes the current record (by id) when editing.
 * @param {string} registration
 * @param {Object[]} existingAircraft
 * @param {string|null} [currentId] - ID of the aircraft being edited (excluded from check)
 * @returns {boolean} true if registration is unique
 */
export function isRegistrationUnique(registration, existingAircraft, currentId = null) {
  const normalised = registration.trim().toUpperCase();
  return !existingAircraft.some(
    (a) =>
      a.registration.trim().toUpperCase() === normalised &&
      a.id !== currentId,
  );
}

/**
 * Filters out retired aircraft, returning only active ones.
 * An aircraft is retired if meta.retired === true.
 * @param {Object[]} aircraftList
 * @returns {Object[]}
 */
export function filterActiveAircraft(aircraftList) {
  return aircraftList.filter((a) => !(a.meta && a.meta.retired === true));
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * createAircraftManagement() → HTMLElement
 *
 * Returns a section element containing the aircraft management Data_Grid
 * with add, edit, retire capabilities.
 */
export function createAircraftManagement() {
  const section = document.createElement('section');
  section.className = 'settings-section aircraft-management';

  /** @type {Object[]} */
  let aircraft = [];
  let showRetired = false;

  const gridContainer = document.createElement('div');
  section.appendChild(gridContainer);

  // Toggle for retired aircraft visibility
  const toggleContainer = document.createElement('div');
  toggleContainer.className = 'aircraft-management__toggle';
  const toggleLabel = document.createElement('label');
  const toggleCheckbox = document.createElement('input');
  toggleCheckbox.type = 'checkbox';
  toggleCheckbox.checked = false;
  toggleCheckbox.addEventListener('change', () => {
    showRetired = toggleCheckbox.checked;
    renderGrid();
  });
  toggleLabel.appendChild(toggleCheckbox);
  toggleLabel.appendChild(document.createTextNode(' Show retired aircraft'));
  toggleContainer.appendChild(toggleLabel);
  // toggleContainer will be inserted into the grid's subheader in renderGrid()

  // Message area for success/error feedback
  const messageArea = document.createElement('div');
  messageArea.className = 'aircraft-management__message';
  section.appendChild(messageArea);

  /**
   * Show a temporary message.
   * @param {string} text
   * @param {'success'|'error'} type
   */
  function showMessage(text, type = 'success') {
    messageArea.textContent = text;
    messageArea.className = `aircraft-management__message aircraft-management__message--${type}`;
    setTimeout(() => {
      messageArea.textContent = '';
      messageArea.className = 'aircraft-management__message';
    }, 4000);
  }

  /**
   * Get the display data based on retired filter.
   */
  function getDisplayData() {
    if (showRetired) return aircraft;
    return filterActiveAircraft(aircraft);
  }

  /**
   * Build and render the Data_Grid with current aircraft data.
   */
  function renderGrid() {
    gridContainer.innerHTML = '';

    const grid = createDataGrid({
      columns: [
        { key: 'registration', label: 'Registration' },
        { key: 'type', label: 'Type' },
        { key: 'name', label: 'Name' },
        {
          key: 'status',
          label: 'Status',
          format: (v) => v || 'SERVICEABLE',
        },
        {
          key: 'total_hours',
          label: 'Total Hours',
          align: 'right',
          format: (v) => (v != null ? Number(v).toFixed(1) : '0.0'),
        },
        {
          key: 'hours_since_last_50hr',
          label: 'Hours Note',
          align: 'right',
          html: true,
          format: formatHoursNote,
        },
      ],
      data: getDisplayData(),
      defaultSort: 'registration',
      defaultOrder: 'asc',
      emptyMessage: 'No aircraft found',
      filterable: true,
      heading: 'Aircraft Management',
      addLabel: 'Add Aircraft',
      onAdd: () => openAddAircraftModal(),
      onRowClick: (row) => openEditAircraftModal(row),
      rowClass: (row) => getAircraftRowClass(row),
    });

    gridContainer.appendChild(grid);

    // Insert retired aircraft toggle into a toolbar row after the header row
    const toolbarRow = document.createElement('div');
    toolbarRow.className = 'settings-section__toolbar-row';
    toolbarRow.appendChild(toggleContainer);

    const headerRowEl = grid.querySelector('.data-grid__header-row');
    if (headerRowEl && headerRowEl.nextSibling) {
      grid.insertBefore(toolbarRow, headerRowEl.nextSibling);
    } else {
      grid.appendChild(toolbarRow);
    }
  }

  /**
   * Open the "Add Aircraft" modal.
   */
  function openAddAircraftModal() {
    // Tank list editor as custom section
    const tankHeading = document.createElement('h4');
    tankHeading.textContent = 'Fuel & Oil Tanks';
    tankHeading.style.margin = '1rem 0 0.5rem';

    const tankEditor = createTankListEditor();

    const tankSection = document.createElement('div');
    tankSection.appendChild(tankHeading);
    tankSection.appendChild(tankEditor);

    openDetailModal({
      title: 'Add Aircraft',
      fields: [
        { name: 'registration', label: 'Registration', type: 'text', required: true },
        { name: 'type', label: 'Type', type: 'text', required: true },
        { name: 'name', label: 'Name', type: 'text', required: true },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          options: [
            { value: 'SERVICEABLE', label: 'Serviceable' },
            { value: 'UNSERVICEABLE', label: 'Unserviceable' },
          ],
        },
        { name: 'airframe_hours', label: 'Initial Airframe Hours', type: 'number' },
        { name: 'engine_hours', label: 'Initial Engine Hours', type: 'number' },
        { name: 'propeller_hours', label: 'Initial Propeller Hours', type: 'number' },
        {
          name: 'fuel_flow_unit',
          label: 'Fuel Flow Unit',
          type: 'select',
          options: FUEL_FLOW_UNITS.map(u => ({ value: u.value, label: u.label })),
        },
      ],
      customSections: [tankSection],
      onSave: async (formData) => {
        const validation = validateAircraftPayload({
          registration: formData.registration,
          type: formData.type,
          name: formData.name,
        });
        if (!validation.valid) {
          const err = new Error(validation.errors[0]);
          err.fieldErrors = {};
          validation.errors.forEach((e) => {
            if (e.includes('registration')) err.fieldErrors.registration = e;
            else if (e.includes('type')) err.fieldErrors.type = e;
            else if (e.includes('name')) err.fieldErrors.name = e;
          });
          throw err;
        }

        if (!isRegistrationUnique(formData.registration, aircraft)) {
          const err = new Error('An aircraft with this registration already exists');
          err.fieldErrors = { registration: 'An aircraft with this registration already exists' };
          throw err;
        }

        const fuelTankConfig = tankEditor.getTankConfig();

        const defaults = getDefaultAircraftValues();
        try {
          await createAircraft({
            registration: formData.registration.trim(),
            type: formData.type.trim(),
            name: formData.name.trim(),
            status: formData.status || defaults.status,
            airframe_hours: formData.airframe_hours ? Number(formData.airframe_hours) : defaults.airframe_hours,
            engine_hours: formData.engine_hours ? Number(formData.engine_hours) : defaults.engine_hours,
            propeller_hours: formData.propeller_hours ? Number(formData.propeller_hours) : defaults.propeller_hours,
            hours_since_last_50hr: defaults.hours_since_last_50hr,
            fuel_tank_config: fuelTankConfig,
            fuel_flow_unit: formData.fuel_flow_unit || 'GPH',
          });
          await refreshAircraft();
          showMessage('Aircraft created successfully');
        } catch (apiErr) {
          const msg = apiErr.message || '';
          if (msg.includes('23505') || msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already')) {
            const err = new Error('An aircraft with this registration already exists');
            err.fieldErrors = { registration: 'An aircraft with this registration already exists' };
            throw err;
          }
          throw apiErr;
        }
      },
    });
  }

  /**
   * Open the edit/detail modal for an existing aircraft.
   * @param {Object} ac - The aircraft record
   */
  function openEditAircraftModal(ac) {
    // Build custom section for Retire button
    const actionDiv = document.createElement('div');
    actionDiv.className = 'aircraft-detail__actions';

    const isRetired = ac.meta && ac.meta.retired === true;

    if (!isRetired) {
      const retireBtn = document.createElement('button');
      retireBtn.type = 'button';
      retireBtn.className = 'btn btn--danger';
      retireBtn.textContent = 'Retire';
      retireBtn.addEventListener('click', async () => {
        try {
          await retireAircraft(ac.id);
          await refreshAircraft();
          showMessage(`${ac.registration} has been retired`);
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
        } catch (err) {
          showMessage(err.message || 'Failed to retire aircraft', 'error');
        }
      });
      actionDiv.appendChild(retireBtn);
    } else {
      const retiredBadge = document.createElement('span');
      retiredBadge.className = 'aircraft-detail__retired-badge';
      retiredBadge.textContent = 'RETIRED';
      actionDiv.appendChild(retiredBadge);
    }

    // Tank list editor pre-populated with existing config
    const tankHeading = document.createElement('h4');
    tankHeading.textContent = 'Fuel & Oil Tanks';
    tankHeading.style.margin = '1rem 0 0.5rem';

    const tankEditor = createTankListEditor({ config: ac.fuel_tank_config });

    const tankSection = document.createElement('div');
    tankSection.appendChild(tankHeading);
    tankSection.appendChild(tankEditor);

    openDetailModal({
      title: `Edit Aircraft - ${ac.registration}`,
      fields: [
        { name: 'registration', label: 'Registration', type: 'text', required: true },
        { name: 'type', label: 'Type', type: 'text', required: true },
        { name: 'name', label: 'Name', type: 'text', required: true },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          required: true,
          options: [
            { value: 'SERVICEABLE', label: 'Serviceable' },
            { value: 'UNSERVICEABLE', label: 'Unserviceable' },
          ],
        },
        { name: 'airframe_hours', label: 'Airframe Hours', type: 'number' },
        { name: 'engine_hours', label: 'Engine Hours', type: 'number' },
        { name: 'propeller_hours', label: 'Propeller Hours', type: 'number' },
        { name: 'hours_since_last_50hr', label: 'Hrs Since 50hr Check', type: 'number' },
        {
          name: 'fuel_flow_unit',
          label: 'Fuel Flow Unit',
          type: 'select',
          options: FUEL_FLOW_UNITS.map(u => ({ value: u.value, label: u.label })),
        },
      ],
      values: {
        registration: ac.registration,
        type: ac.type,
        name: ac.name,
        status: ac.status,
        airframe_hours: ac.airframe_hours,
        engine_hours: ac.engine_hours,
        propeller_hours: ac.propeller_hours,
        hours_since_last_50hr: ac.hours_since_last_50hr,
        fuel_flow_unit: ac.fuel_flow_unit || 'GPH',
      },
      customSections: [tankSection, actionDiv],
      onSave: async (formData) => {
        const validation = validateAircraftPayload({
          registration: formData.registration,
          type: formData.type,
          name: formData.name,
          airframe_hours: formData.airframe_hours ? Number(formData.airframe_hours) : undefined,
          engine_hours: formData.engine_hours ? Number(formData.engine_hours) : undefined,
          propeller_hours: formData.propeller_hours ? Number(formData.propeller_hours) : undefined,
          hours_since_last_50hr: formData.hours_since_last_50hr ? Number(formData.hours_since_last_50hr) : undefined,
        });
        if (!validation.valid) {
          const err = new Error(validation.errors[0]);
          err.fieldErrors = {};
          validation.errors.forEach((e) => {
            const field = ['registration', 'type', 'name', 'airframe_hours', 'engine_hours', 'propeller_hours', 'hours_since_last_50hr']
              .find((f) => e.includes(f));
            if (field) err.fieldErrors[field] = e;
          });
          throw err;
        }

        if (!isRegistrationUnique(formData.registration, aircraft, ac.id)) {
          const err = new Error('An aircraft with this registration already exists');
          err.fieldErrors = { registration: 'An aircraft with this registration already exists' };
          throw err;
        }

        const fuelTankConfig = tankEditor.getTankConfig();

        try {
          await updateAircraft(ac.id, {
            registration: formData.registration.trim(),
            type: formData.type.trim(),
            name: formData.name.trim(),
            status: formData.status,
            airframe_hours: formData.airframe_hours ? Number(formData.airframe_hours) : ac.airframe_hours,
            engine_hours: formData.engine_hours ? Number(formData.engine_hours) : ac.engine_hours,
            propeller_hours: formData.propeller_hours ? Number(formData.propeller_hours) : ac.propeller_hours,
            hours_since_last_50hr: formData.hours_since_last_50hr ? Number(formData.hours_since_last_50hr) : ac.hours_since_last_50hr,
            fuel_tank_config: fuelTankConfig,
            fuel_flow_unit: formData.fuel_flow_unit || ac.fuel_flow_unit || 'GPH',
          });
          await refreshAircraft();
          showMessage('Aircraft updated successfully');
        } catch (apiErr) {
          const msg = apiErr.message || '';
          if (msg.includes('23505') || msg.toLowerCase().includes('duplicate')) {
            const err = new Error('An aircraft with this registration already exists');
            err.fieldErrors = { registration: 'An aircraft with this registration already exists' };
            throw err;
          }
          throw apiErr;
        }
      },
    });
  }

  /**
   * Fetch aircraft from the API and re-render the grid.
   */
  async function refreshAircraft() {
    try {
      aircraft = await getAircraft();
    } catch (err) {
      aircraft = [];
      showMessage('Failed to load aircraft', 'error');
    }
    renderGrid();
  }

  // Initial load
  refreshAircraft();

  return section;
}
